/**
 * Eigenbau: Zeitplanung — Kapazität, Abwesenheit und iCal-Kalender.
 *
 * Früher zwei getrennte Reiter ohne gemeinsamen Kontext. Beides gehört zur
 * persönlichen Zeit: verfügbare Minuten, Abwesenheiten und der Kalender-Feed.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  istGueltigerKalendertag,
  kalendertagInZeitzone,
} from "@cubic/shared";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Leerzustand } from "@/components/Leerzustand";
import {
  baueKalenderFeedAdresse,
  minutenAusEingabe,
} from "@/lib/kalender-kapazitaet";
import { api } from "@/lib/api";
import { useIch } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  Erfolgsmeldung,
  FehlerBlock,
  LadenZeile,
  meldungFuer,
} from "./gemeinsam";

export function EinstellungenZeitplanung() {
  const client = useQueryClient();
  const ich = useIch();
  const eigenePerson = useQuery({
    queryKey: ["cubic", "person", ich.data?.personId],
    queryFn: () => api.personen.lies.query({ id: ich.data!.personId }),
    enabled: Boolean(ich.data?.personId),
  });

  const browserZeitzone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const heute = kalendertagInZeitzone(browserZeitzone);
  const [tag, setzeTag] = useState(heute);
  const [minuten, setzeMinuten] = useState("480");
  const [von, setzeVon] = useState(heute);
  const [bis, setzeBis] = useState(heute);
  const [grund, setzeGrund] = useState("");
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [fehler, setzeFehler] = useState<Meldung | null>(null);
  const [datumBeruehrt, setzeDatumBeruehrt] = useState(false);
  const [neuerFeed, setzeNeuenFeed] = useState<{
    id: string;
    token: string;
  } | null>(null);

  useEffect(() => {
    if (!eigenePerson.data?.zeitzone || datumBeruehrt) return;
    const tagDerPerson = kalendertagInZeitzone(eigenePerson.data.zeitzone);
    setzeTag(tagDerPerson);
    setzeVon(tagDerPerson);
    setzeBis(tagDerPerson);
  }, [datumBeruehrt, eigenePerson.data?.zeitzone]);

  const minutenWert = minutenAusEingabe(minuten);
  const tagGueltig = istGueltigerKalendertag(tag);
  const zeitraumGueltig =
    istGueltigerKalendertag(von) &&
    istGueltigerKalendertag(bis) &&
    von <= bis;

  const lesen = useQuery({
    queryKey: ["cubic", "kapazitaet", tag],
    queryFn: () => api.kapazitaet.lesen.query({ tag }),
    enabled: tagGueltig,
  });

  const feeds = useQuery({
    queryKey: ["cubic", "kalender", "liste"],
    queryFn: () => api.kalender.liste.query(),
  });

  const setzen = useMutation({
    mutationFn: () => {
      if (minutenWert === null || !tagGueltig) {
        throw new Error("Tag oder Minuten sind ungültig.");
      }
      return api.kapazitaet.setzen.mutate({ tag, minuten: minutenWert });
    },
    onSuccess: async () => {
      setzeFehler(null);
      setzeMeldung("Kapazität gespeichert.");
      await lesen.refetch();
    },
    onError: (e) => {
      setzeMeldung(null);
      setzeFehler(meldungFuer(e, "Kapazität konnte nicht gespeichert werden."));
    },
  });

  const abwesenheit = useMutation({
    mutationFn: () => {
      if (!zeitraumGueltig) throw new Error("Der Zeitraum ist ungültig.");
      return api.kapazitaet.abwesenheit.mutate({
        vonTag: von,
        bisTag: bis,
        grund: grund || undefined,
      });
    },
    onSuccess: async () => {
      setzeFehler(null);
      setzeMeldung("Abwesenheit eingetragen.");
      if (tag >= von && tag <= bis) await lesen.refetch();
    },
    onError: (e) => {
      setzeMeldung(null);
      setzeFehler(
        meldungFuer(e, "Abwesenheit konnte nicht eingetragen werden."),
      );
    },
  });

  const erzeugen = useMutation({
    mutationFn: () => api.kalender.erzeugen.mutate(),
    onSuccess: async (r) => {
      setzeNeuenFeed(r);
      setzeFehler(null);
      setzeMeldung(null);
      await client.invalidateQueries({ queryKey: ["cubic", "kalender"] });
    },
    onError: (e) => {
      setzeMeldung(null);
      setzeFehler(meldungFuer(e, "Feed konnte nicht erzeugt werden."));
    },
  });

  const widerrufen = useMutation({
    mutationFn: (id: string) => api.kalender.widerrufen.mutate({ id }),
    onSuccess: async (_daten, id) => {
      if (neuerFeed?.id === id) setzeNeuenFeed(null);
      setzeFehler(null);
      setzeMeldung("Feed widerrufen.");
      await client.invalidateQueries({ queryKey: ["cubic", "kalender"] });
    },
    onError: (e) => {
      setzeMeldung(null);
      setzeFehler(meldungFuer(e, "Feed konnte nicht widerrufen werden."));
    },
  });

  const seitenUrsprung =
    typeof globalThis.location !== "undefined"
      ? globalThis.location.origin
      : "http://localhost:5173";
  const apiAdresse =
    import.meta.env["VITE_API_URL"] ?? "http://localhost:3001/trpc";
  const feedAdresse = neuerFeed
    ? baueKalenderFeedAdresse(neuerFeed.token, apiAdresse, seitenUrsprung)
    : null;

  async function kopiereAdresse() {
    if (!feedAdresse) return;
    try {
      await globalThis.navigator.clipboard.writeText(feedAdresse);
      setzeFehler(null);
      setzeMeldung("Abonnement-Adresse kopiert.");
    } catch (e) {
      setzeFehler(meldungFuer(e, "Adresse konnte nicht kopiert werden."));
    }
  }

  const profilFehler = ich.error ?? eigenePerson.error;
  const zeitzone = eigenePerson.data?.zeitzone ?? browserZeitzone;

  return (
    <EinstellungenHuelle
      aktiv="zeitplanung"
      beschreibung="Verfügbare Minuten, Abwesenheiten und dein iCal-Abonnement."
    >
      {fehler ? <Fehlerzeile meldung={fehler} /> : null}
      {!fehler && profilFehler ? (
        <FehlerBlock
          fehler={profilFehler}
          fallback="Die persönliche Zeitzone konnte nicht geladen werden."
          onErneut={() => {
            void ich.refetch();
            void eigenePerson.refetch();
          }}
        />
      ) : null}
      <Erfolgsmeldung text={meldung} />

      <EinstellungsAbschnitt
        titel="Tageskapazität"
        beschreibung={`Kalendertage folgen deiner Zeitzone ${zeitzone}.`}
      >
        <div className="flex flex-wrap gap-3">
          <div>
            <Label htmlFor="kap-tag">Tag</Label>
            <Input
              id="kap-tag"
              className="mt-1.5"
              type="date"
              value={tag}
              required
              onChange={(e) => {
                setzeDatumBeruehrt(true);
                setzeTag(e.target.value);
                setzeMeldung(null);
                setzeFehler(null);
              }}
            />
          </div>
          <div>
            <Label htmlFor="kap-min">Minuten</Label>
            <Input
              id="kap-min"
              className="mt-1.5"
              type="number"
              value={minuten}
              min={0}
              max={24 * 60}
              step={1}
              required
              onChange={(e) => {
                setzeMinuten(e.target.value);
                setzeMeldung(null);
                setzeFehler(null);
              }}
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => setzen.mutate()}
              disabled={
                setzen.isPending || minutenWert === null || !tagGueltig
              }
              isLoading={setzen.isPending}
            >
              Speichern
            </Button>
          </div>
        </div>
        <p className="text-[12.5px] text-tinte3">
          Verfügbar an diesem Tag:{" "}
          {!tagGueltig
            ? "gültigen Tag wählen"
            : lesen.isLoading
              ? "…"
              : lesen.data
                ? `${lesen.data.minuten} Min.`
                : "nicht verfügbar"}
        </p>
        {lesen.isError ? (
          <FehlerBlock
            fehler={lesen.error}
            fallback="Verfügbare Zeit konnte nicht geladen werden."
            onErneut={() => void lesen.refetch()}
          />
        ) : null}
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Abwesenheit"
        beschreibung="Zeiten, an denen du nicht verfügbar bist — über alle Projekte hinweg."
      >
        <div className="flex flex-wrap gap-3">
          <div>
            <Label htmlFor="ab-von">Von</Label>
            <Input
              id="ab-von"
              className="mt-1.5"
              type="date"
              value={von}
              required
              onChange={(e) => {
                setzeDatumBeruehrt(true);
                setzeVon(e.target.value);
                setzeMeldung(null);
                setzeFehler(null);
              }}
            />
          </div>
          <div>
            <Label htmlFor="ab-bis">Bis</Label>
            <Input
              id="ab-bis"
              className="mt-1.5"
              type="date"
              value={bis}
              min={von || undefined}
              required
              onChange={(e) => {
                setzeDatumBeruehrt(true);
                setzeBis(e.target.value);
                setzeMeldung(null);
                setzeFehler(null);
              }}
            />
          </div>
          <div className="min-w-[12rem] flex-1">
            <Label htmlFor="ab-grund">Grund</Label>
            <Input
              id="ab-grund"
              className="mt-1.5"
              value={grund}
              maxLength={500}
              onChange={(e) => {
                setzeGrund(e.target.value);
                setzeMeldung(null);
                setzeFehler(null);
              }}
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => abwesenheit.mutate()}
              disabled={abwesenheit.isPending || !zeitraumGueltig}
              isLoading={abwesenheit.isPending}
            >
              Eintragen
            </Button>
          </div>
        </div>
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Kalender-Abonnement"
        beschreibung="iCal-Feed deiner fälligen Aufgaben und Meilensteine. Der Token erscheint nur einmal beim Erzeugen."
        aktion={
          <Button
            onClick={() => {
              setzeMeldung(null);
              erzeugen.mutate();
            }}
            disabled={erzeugen.isPending || widerrufen.isPending}
            isLoading={erzeugen.isPending}
          >
            Neuen Feed erzeugen
          </Button>
        }
      >
        {feedAdresse ? (
          <div className="space-y-2 bg-wisch p-4 text-sm">
            <p className="font-medium text-tinte">
              Abonnement-Adresse (jetzt speichern):
            </p>
            <code className="block break-all font-mono text-[11.5px] text-tinte2">
              {feedAdresse}
            </code>
            <Button
              variant="secondary"
              onClick={() => void kopiereAdresse()}
            >
              Adresse kopieren
            </Button>
          </div>
        ) : null}

        {feeds.isLoading ? (
          <LadenZeile text="Lade Feeds …" />
        ) : feeds.isError ? (
          <FehlerBlock
            fehler={feeds.error}
            fallback="Feeds konnten nicht geladen werden."
            onErneut={() => void feeds.refetch()}
          />
        ) : (feeds.data?.length ?? 0) === 0 ? (
          <Leerzustand
            titel="Noch kein Feed."
            hinweis="Erzeuge einen Feed und abonniere ihn in deinem Kalenderprogramm."
          />
        ) : (
          <ul className="divide-y divide-linie">
            {feeds.data!.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between gap-3 py-3 text-sm"
              >
                <span className="flex items-center gap-2">
                  <span className="text-tinte2">
                    {new Date(f.angelegtAm).toLocaleString("de-DE")}
                  </span>
                  {f.widerrufenAm ? (
                    <Badge variant="neutral">widerrufen</Badge>
                  ) : (
                    <Badge variant="success">aktiv</Badge>
                  )}
                </span>
                {!f.widerrufenAm ? (
                  <Button
                    variant="secondary"
                    onClick={() => widerrufen.mutate(f.id)}
                    disabled={widerrufen.isPending || erzeugen.isPending}
                    isLoading={
                      widerrufen.isPending && widerrufen.variables === f.id
                    }
                  >
                    Widerrufen
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </EinstellungsAbschnitt>
    </EinstellungenHuelle>
  );
}
